import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { guidance, guest as guestApi } from "../lib/api";
import {
  ensureGuest,
  appendExchange,
  recentTurns,
  getGuest,
  trialDaysLeft,
  trialHoursLeft,
} from "../lib/guest";
import GuidanceResponse from "../components/GuidanceResponse";
import ReturnToProverb, { setSafeProverbReturnUrl } from "../components/ReturnToProverb";
import {
  Sparkles,
  ArrowRight,
  Heart,
  Compass,
  Loader2,
  CheckCircle2,
  MessageCircleHeart,
} from "lucide-react";

const EXAMPLE_SCENARIOS = [
  "I'm stressed about a decision I need to make and I'm not sure what to do.",
  "I just yelled at my kids and feel like a terrible parent.",
  "I need to text my mom about missing thanksgiving without hurting her.",
  "I feel lost and don't know what to do next.",
];

export default function WelcomePage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [followBusy, setFollowBusy] = useState(false);
  const [error, setError] = useState(null);
  const [daysLeft, setDaysLeft] = useState(3);
  const [hoursLeft, setHoursLeft] = useState(72);
  // "Say It Better" deep-link intent from I Need a Proverb. Read from the
  // non-sensitive `dest`/`feature` query param — allowlisted so a random
  // value cannot flip us into a made-up branch. Sensitive fields (return
  // URL etc.) go through the signed SSO token, not query params.
  const [fromProverb, setFromProverb] = useState(false);
  const [sayItBetterIntent, setSayItBetterIntent] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const isFromProverb =
      params.get("from") === "proverb" ||
      params.get("source") === "proverb" ||
      !!params.get("ref");
    const topic = params.get("topic") || params.get("t") || null;
    const destParam = (params.get("dest") || params.get("feature") || "").toLowerCase();
    const wantsSayItBetter = destParam === "say-it-better";
    // Optional safe return URL — validated against the ineedaproverb.com
    // host allowlist inside setSafeProverbReturnUrl. Anything else is
    // silently dropped, so we can pass raw query values through.
    setSafeProverbReturnUrl(params.get("return_url") || params.get("return"));

    // Authenticated session ALWAYS wins over anonymous guest state.
    //
    // If the visitor already has a LifeGuide auth token, route on their
    // *account* entitlement (Pro / active trial / expired), never on
    // guest-trial localStorage. This prevents a stale expired `lg_guest`
    // record from blocking a genuinely-paid account (see Case A in the
    // "auth priority" fix). We deliberately skip `ensureGuest()` here
    // so no new anonymous trial is created for a signed-in user.
    const hasAuthToken = !!localStorage.getItem("lg_token");
    if (wantsSayItBetter && hasAuthToken) {
      navigate("/analyze", { replace: true });
      return;
    }

    // Anonymous branch below: only executed when NOT authenticated.
    const g = ensureGuest({
      ...(isFromProverb ? { from_proverb: true } : {}),
      ...(topic ? { proverb_topic: topic } : {}),
    });
    setFromProverb(isFromProverb || !!g?.from_proverb);
    setSayItBetterIntent(wantsSayItBetter);
    setDaysLeft(trialDaysLeft());
    setHoursLeft(trialHoursLeft());
    if (!sessionStorage.getItem("lg_guest_visit_sent") && g?.id) {
      guestApi.track({
        event_type: "guest_visit",
        guest_id: g.id,
        from_proverb: !!g.from_proverb,
        proverb_topic: g.proverb_topic || null,
      });
      sessionStorage.setItem("lg_guest_visit_sent", "1");
    }

    // Anonymous Say-It-Better auto-entry: the 3-day guest trial has just
    // been initialized above; drop the visitor straight into the tool.
    if (wantsSayItBetter) {
      navigate("/guest?tab=say-it-better", { replace: true });
    }
  }, [params, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    const trimmed = text.trim();
    if (trimmed.length < 4) {
      toast.error("Share a little more so LifeGuide can meet you where you are.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const r = await guidance.sample(trimmed);
      setResult(r);
      const cur = getGuest();
      if (cur) {
        appendExchange({ role: "user", kind: "guidance", content: trimmed });
        appendExchange({
          role: "assistant",
          kind: "guidance",
          content: r?.follow_up_question || r?.next_step || "",
        });
        guestApi.track({
          event_type: "guest_guidance",
          guest_id: cur.id,
          from_proverb: !!cur.from_proverb,
        });
      }
      requestAnimationFrame(() => {
        document
          .getElementById("welcome-result")
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(detail || "Something went sideways — please try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  const runFollowUp = async (followText) => {
    if (followBusy) return;
    setFollowBusy(true);
    try {
      const history = recentTurns("guidance", 8);
      const r = await guidance.sample(followText, history);
      appendExchange({ role: "user", kind: "guidance", content: followText });
      appendExchange({
        role: "assistant",
        kind: "guidance",
        content: r?.follow_up_question || r?.next_step || "",
      });
      setResult(r);
      requestAnimationFrame(() => {
        document
          .getElementById("welcome-result")
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Something went sideways — please try again in a moment.");
    } finally {
      setFollowBusy(false);
    }
  };

  const handleGuidanceAction = (action) => {
    if (action === "say_it_better") {
      navigate("/guest?tab=say-it-better");
      return;
    }
    const routeTab =
      action === "make_plan" || action === "continue_talking"
        ? "coach"
        : action === "journal_this"
        ? "journal"
        : action === "save_wisdom"
        ? "wisdom"
        : "guidance";
    navigate(`/guest?tab=${routeTab}`);
  };

  // Say-It-Better deep-link auto-entry: render a blank canvas during the
  // redirect so the trial-gate hero never flashes on screen. Computed
  // synchronously from the URL params (not state) so the very first paint
  // is already the empty placeholder, not the hero. The actual navigation
  // happens in useEffect above.
  const _destNow = (params.get("dest") || params.get("feature") || "").toLowerCase();
  if (_destNow === "say-it-better") {
    return (
      <div
        className="min-h-screen bg-[#F9FAF7]"
        data-testid="welcome-sib-redirecting"
      />
    );
  }

  return (
    <div className="min-h-screen text-[#2E2E2E]">
      <header className="border-b border-[#DCE5DB]/70 bg-white/70 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="welcome-home-link">
            <div className="w-9 h-9 rounded-2xl bg-[#4F6F52] flex items-center justify-center shadow-[0_4px_14px_rgba(79,111,82,0.25)]">
              <Sparkles size={16} className="text-[#F9FAF7]" strokeWidth={1.8} />
            </div>
            <span className="font-display text-xl text-[#4F6F52]">LifeGuide</span>
          </Link>
          <div className="flex items-center gap-4 sm:gap-5">
            {fromProverb && (
              <ReturnToProverb
                testid="welcome-return-to-proverb"
                className="text-sm text-[#6B6B6B] hover:text-[#4F6F52] transition inline-flex items-center gap-1.5"
              />
            )}
            <Link
              to="/sms"
              data-testid="welcome-sms-link"
              className="text-sm text-[#6B6B6B] hover:text-[#4F6F52]"
            >
              SMS Updates
            </Link>
            <Link
              to="/login"
              className="text-sm text-[#6B6B6B] hover:text-[#4F6F52]"
              data-testid="welcome-signin"
            >
              Already have an account?{" "}
              <span className="text-[#4F6F52] underline underline-offset-4">Sign in</span>
            </Link>
          </div>
        </div>
      </header>

      <section className="max-w-3xl mx-auto px-6 pt-14 sm:pt-20 pb-10 text-center fade-in-up">
        <div
          className="inline-flex items-center gap-2 rounded-full bg-[#E1ECDD] border border-[#4F6F52]/20 px-4 py-1.5 mb-6"
          data-testid="welcome-trial-eyebrow"
        >
          <CheckCircle2 size={13} className="text-[#4F6F52]" />
          <span className="text-xs uppercase tracking-[0.18em] text-[#4F6F52] font-semibold">
            3-Day Pro Trial · All features unlocked
          </span>
        </div>

        {sayItBetterIntent ? (
          <div data-testid="welcome-sib-intent">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[#4F6F52]/10 mb-5">
              <MessageCircleHeart size={24} className="text-[#4F6F52]" strokeWidth={1.7} />
            </div>
            <h1 className="font-display text-4xl sm:text-5xl leading-[1.05] text-[#2E2E2E] mb-5">
              You came here to use{" "}
              <span className="text-[#4F6F52]">Say It Better</span>.
            </h1>
            <p className="text-lg text-[#6B6B6B] leading-relaxed max-w-xl mx-auto mb-6">
              Start your free 3-day Pro trial and we'll take you directly there.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/guest?tab=say-it-better"
                data-testid="welcome-sib-cta"
                className="inline-flex items-center gap-2 bg-[#4F6F52] hover:bg-[#3F5A42] text-white rounded-full px-6 py-3.5 font-medium transition shadow-[0_6px_20px_rgba(79,111,82,0.22)]"
              >
                Start Free Trial &amp; Open Say It Better
                <ArrowRight size={15} strokeWidth={2} />
              </Link>
            </div>
            <p className="text-xs text-[#6B6B6B] mt-4">
              No account. No signup. No credit card.
            </p>
          </div>
        ) : (
          <>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#2E2E2E] mb-6">
              Some things need more than a Proverb.
              <br />
              <span className="text-[#4F6F52]">Talk it through.</span>
            </h1>
            <p className="text-lg text-[#6B6B6B] leading-relaxed max-w-xl mx-auto mb-6">
              LifeGuide is a private place to think, reflect, and find your next
              step — biblical wisdom paired with practical guidance. Try it below.
              No account, no signup, no credit card.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/guest"
                data-testid="welcome-hero-guest-btn"
                className="inline-flex items-center gap-2 bg-[#4F6F52] hover:bg-[#3F5A42] text-white rounded-full px-6 py-3 font-medium transition shadow-[0_6px_20px_rgba(79,111,82,0.22)]"
              >
                Start your 3-day Pro trial
                <ArrowRight size={14} strokeWidth={2} />
              </Link>
              <a
                href="#welcome-try-card"
                data-testid="welcome-hero-sample-link"
                className="text-sm text-[#6B6B6B] hover:text-[#4F6F52] px-4 py-2"
              >
                or try a quick sample below
              </a>
            </div>
          </>
        )}
      </section>

      <section className="max-w-3xl mx-auto px-6 pb-16 sm:pb-20">
        <div
          id="welcome-try-card"
          className="rounded-3xl bg-white border border-[#DCE5DB] p-6 sm:p-8 shadow-[0_6px_28px_rgba(0,0,0,0.04)] scroll-mt-24"
          data-testid="welcome-try-card"
        >
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-[#7FA7B0] font-semibold mb-3">
            <Compass size={13} />
            For moments like
          </div>
          <ul className="space-y-1.5 text-[15px] text-[#2E2E2E] leading-relaxed mb-6">
            {EXAMPLE_SCENARIOS.map((s, i) => (
              <li
                key={i}
                className="flex gap-2 items-start cursor-pointer hover:text-[#4F6F52] transition"
                onClick={() => setText(s)}
                data-testid={`welcome-example-${i}`}
                role="button"
              >
                <span className="text-[#4F6F52] mt-1">·</span>
                <span>"{s}"</span>
              </li>
            ))}
          </ul>

          <form onSubmit={submit} className="space-y-4">
            <label className="block text-sm text-[#6B6B6B] mb-1">
              What's happening in your world right now?
            </label>
            <textarea
              data-testid="welcome-guidance-input"
              rows={4}
              maxLength={1000}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Tap an example above or type your own. Everything stays private."
              className="w-full bg-[#F9FAF7] border border-[#DCE5DB] rounded-2xl px-4 py-3.5 focus:outline-none focus:border-[#4F6F52] focus:ring-2 focus:ring-[#4F6F52]/15 transition resize-none text-[15px] leading-relaxed"
            />
            <button
              type="submit"
              disabled={busy || text.trim().length < 4}
              data-testid="welcome-try-btn"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#4F6F52] hover:bg-[#3F5A42] disabled:opacity-60 text-white rounded-full py-4 font-medium transition shadow-[0_6px_20px_rgba(79,111,82,0.22)]"
            >
              {busy ? (
                <>
                  <Loader2 size={15} className="animate-spin" /> LifeGuide is listening…
                </>
              ) : (
                <>
                  Try LifeGuide <ArrowRight size={15} strokeWidth={2} />
                </>
              )}
            </button>
            <p className="text-xs text-[#6B6B6B] text-center leading-relaxed">
              No account, no signup, no credit card required.
            </p>
          </form>

          {error && (
            <div
              data-testid="welcome-error"
              className="mt-4 rounded-2xl bg-[#F5E6D3]/60 border border-[#A86B4A]/30 px-4 py-3 text-sm text-[#A86B4A]"
            >
              {error}
            </div>
          )}
        </div>
      </section>

      {result && (
        <section
          id="welcome-result"
          className="max-w-3xl mx-auto px-6 pb-20 scroll-mt-24 fade-in-up"
          data-testid="welcome-result-section"
        >
          <GuidanceResponse
            result={result}
            onFollowUp={runFollowUp}
            onAction={handleGuidanceAction}
            busy={followBusy}
            testidPrefix="welcome-guidance"
          />

          <div
            data-testid="welcome-trial-cta"
            className="mt-8 rounded-3xl bg-gradient-to-br from-[#F5EBD9]/60 to-white border border-[#D6C7A1]/60 p-6 sm:p-8 text-center"
          >
            <Heart size={22} className="text-[#8C7A4F] mx-auto mb-3" strokeWidth={1.6} />
            <h2 className="font-display text-2xl sm:text-3xl text-[#2E2E2E] mb-2">
              Want to keep going?
            </h2>
            <p className="text-[#6B6B6B] leading-relaxed max-w-md mx-auto mb-5">
              Every LifeGuide feature is unlocked for the next{" "}
              {daysLeft > 1 ? `${daysLeft} days` : `${hoursLeft} hours`} — no credit card. Create a
              free account whenever you want to save your progress across devices.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/guest"
                data-testid="welcome-continue-guest-btn"
                className="inline-flex items-center gap-2 bg-white border border-[#4F6F52]/40 text-[#4F6F52] hover:bg-[#EEF3EC] rounded-full px-6 py-3.5 font-medium transition"
              >
                Keep exploring as a guest
                <ArrowRight size={14} strokeWidth={2} />
              </Link>
              <Link
                to="/signup?from=guest"
                data-testid="welcome-continue-btn"
                onClick={() => {
                  const cur = getGuest();
                  if (cur) {
                    guestApi.track({
                      event_type: "guest_signup_click",
                      guest_id: cur.id,
                      from_proverb: !!cur.from_proverb,
                    });
                  }
                }}
                className="inline-flex items-center gap-2 bg-[#4F6F52] hover:bg-[#3F5A42] text-white rounded-full px-6 py-3.5 font-medium transition shadow-[0_6px_20px_rgba(79,111,82,0.22)]"
              >
                Create free account
                <ArrowRight size={14} strokeWidth={2} />
              </Link>
            </div>
          </div>
        </section>
      )}

      <footer className="border-t border-[#DCE5DB] py-8 bg-white/50">
        <div className="max-w-6xl mx-auto px-6 text-center text-sm text-[#6B6B6B] flex flex-wrap justify-center gap-x-5 gap-y-1">
          <Link to="/gift" className="hover:text-[#4F6F52]">
            Give LifeGuide
          </Link>
          <Link to="/sms" data-testid="welcome-footer-sms-link" className="hover:text-[#4F6F52]">
            SMS Updates
          </Link>
          <Link to="/privacy" className="hover:text-[#4F6F52]">
            Privacy
          </Link>
          <Link to="/terms" className="hover:text-[#4F6F52]">
            Terms
          </Link>
        </div>
      </footer>
    </div>
  );
}
